import * as React from "react";
import { CopyRegular } from "@fluentui/react-icons";
import { useEmptyStateLandingStyles } from "../styles/emptyStateLanding.styles";

const DOCS_BASE = "https://turbo-telegram-qjwo9wj.pages.github.io";

const sidebarGroups = [
    {
        heading: "Getting started",
        links: [
            { label: "Quickstart", path: "/getting-started/quickstart/" },
            { label: "Install the CLI", path: "/getting-started/install/" },
            { label: "Initialize a project", path: "/getting-started/init/" },
            { label: "Scout, heal & run", path: "/getting-started/scout-heal-run/" },
            { label: "View the dashboard", path: "/getting-started/dashboard/" },
        ],
    },
    {
        heading: "Guides",
        links: [
            { label: "Customization", path: "/guides/customization/" },
            { label: "Custom skills", path: "/guides/custom-skills/" },
            { label: "MFA testing", path: "/guides/mfa-testing/" },
            { label: "Model selection", path: "/guides/models/" },
        ],
    },
    {
        heading: "Reference",
        links: [
            { label: "Customization", path: "/reference/cli/" },
            { label: "Custom skills", path: "/reference/config/" },
            { label: "MFA testing", path: "/guides/mfa-testing/" },
            { label: "Model selection", path: "/guides/models/" },
        ],
    },
];

function CopyButton({ text }: { text: string }) {
    const s = useEmptyStateLandingStyles();
    const [copied, setCopied] = React.useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <button
            className={s.copyBtn}
            onClick={handleCopy}
            title={copied ? "Copied!" : "Copy to clipboard"}
            aria-label="Copy to clipboard"
        >
            <CopyRegular style={{ fontSize: 14 }} />
        </button>
    );
}

const installCode = `# From the repo you want to test
cat >> .npmrc <<'EOF'
@microsoft:registry=https://npm.pkg.github.com
//npm.pkg.github.com/:_authToken=\${GH_PACKAGES_TOKEN}
EOF

# Authenticate (one-time) — the GitHub CLI handles scopes and SSO
gh auth login
export GH_PACKAGES_TOKEN=$(gh auth token)

# Install globally and verify
npm i -g @microsoft/app-testing-agent
ata --version`;

const generateCode = `# Set up your project
# --target is the URL of the app under test. It can be a local dev server, a staging URL, or a public endpoint.
ata init --target http://localhost:3000

# Create end-to-end tests
ata scout`;

const optionsCode = `--target <url>     Target app URL (e.g. http://localhost:3000)
-p, --prompt       Additional instructions to guide the agent
-m, --model        Model to use (default: claude-opus-4.6)
-v, --verbose      Show detailed output
--create-pr        Create a PR with the changes (CI mode)
--new-session      Force a new session`;

const commands = [
    { cmd: "ata init", desc: "Analyze repo and set up .testing-agent/instructions.md" },
    { cmd: "ata heal", desc: "Classify test failures and propose fixes" },
    { cmd: "ata scout", desc: "Propose new tests based on recent changes and coverage gaps" },
    { cmd: "ata run", desc: "Run tests and optionally upload results to the dashboard" },
    { cmd: "ata plan", desc: "Update the test plan without generating tests" },
    { cmd: "ata generate", desc: "Generate Playwright tests from the plan" },
];

/* ---- Sidebar component (used separately in dashboard layout) ---- */
function GettingStartedSidebar() {
    const s = useEmptyStateLandingStyles();
    return (
        <div className={s.sidebar}>
            {sidebarGroups.map((group) => (
                <div key={group.heading} className={s.sidebarCard}>
                    <p className={s.sidebarTitle}>{group.heading}</p>
                    <div className={s.linkGroup}>
                        {group.links.map((link) => (
                            <a
                                key={link.path + link.label}
                                className={s.linkItem}
                                href={`${DOCS_BASE}${link.path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {link.label}
                            </a>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ---- Main card content (placed inside mainContent alongside titleRow) ---- */
const EmptyStateLanding: React.FC & { Sidebar: typeof GettingStartedSidebar } = () => {
    const s = useEmptyStateLandingStyles();

    return (
        <div className={s.card}>
            <div className={s.cardHeader}>
                <h2 className={s.cardHeaderText}>Get started with Testing Agent</h2>
            </div>
            <div className={s.cardDivider} />
            <div className={s.cardBody}>
                <p className={s.heroSubtitle}>
                    Get from zero to a generated, self-healing Playwright suite in about 10 minutes.
                </p>

                {/* Step 1 — Prerequisites */}
                <div className={s.step}>
                    <h3 className={s.stepTitle}>1. Prerequisites</h3>
                    <div className={s.stepBody}>
                        <ul className={s.itemList}>
                            <li className={s.itemEntry}>
                                <strong>Node.js 20+</strong> (<code className={s.inlineCode}>node --version</code>)
                            </li>
                            <li className={s.itemEntry}>
                                <strong>GitHub Copilot</strong> access (the agent uses Copilot for AI reasoning)
                            </li>
                            <li className={s.itemEntry}>
                                <strong>Azure CLI</strong> signed in (<code className={s.inlineCode}>az login</code>) — for Playwright workspace features
                            </li>
                            <li className={s.itemEntry}>
                                <strong>Access to <code className={s.inlineCode}>microsoft/app-testing-agent</code></strong> — ask your team admin to add you during Private Preview
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Step 2 — Install the CLI */}
                <div className={s.step}>
                    <h3 className={s.stepTitle}>2. Install the CLI</h3>
                    <div className={s.stepBody}>
                        <p className={s.stepDescription}>
                            The CLI ships as <code className={s.inlineCode}>@microsoft/app-testing-agent</code> on GitHub Packages. Use a <strong>project-local <code className={s.inlineCode}>.npmrc</code></strong> so only this repo pulls from GitHub Packages — your global npm defaults stay untouched.
                        </p>
                        <div className={s.codeBlock}>
                            <pre className={s.codeText}>
                                <span className={s.syntaxComment}># From the repo you want to test</span>{"\n"}
                                <span>cat </span><span>&gt;&gt; .npmrc &lt;&lt;</span><span className={s.syntaxGreen}>{'\'EOF\''}</span>{"\n"}
                                <span className={s.syntaxGreen}>@microsoft:registry=https://npm.pkg.github.com</span>{"\n"}
                                <span className={s.syntaxGreen}>{"//npm.pkg.github.com/:_authToken=${GH_PACKAGES_TOKEN}"}</span>{"\n"}
                                <span className={s.syntaxGreen}>EOF</span>{"\n"}
                                {"\n"}
                                <span className={s.syntaxComment}># Authenticate (one-time) — the GitHub CLI handles scopes and SSO</span>{"\n"}
                                <span>gh auth login</span>{"\n"}
                                <span className={s.syntaxPurple}>export</span><span> GH_PACKAGES_TOKEN=</span><span className={s.syntaxBlue}>$(</span><span>gh auth token</span><span className={s.syntaxBlue}>)</span>{"\n"}
                                {"\n"}
                                <span className={s.syntaxComment}># Install globally and verify</span>{"\n"}
                                <span>npm i -g @microsoft/app-testing-agent</span>{"\n"}
                                <span>ata --version</span>
                            </pre>
                            <CopyButton text={installCode} />
                        </div>
                        <p className={s.stepDescription}>
                            <span>See </span>
                            <a className={s.stepLink} href={`${DOCS_BASE}/getting-started/install/`} target="_blank" rel="noopener noreferrer">
                                Install the CLI
                            </a>
                            <span> for CI variants and troubleshooting.</span>
                        </p>
                    </div>
                </div>

                {/* Step 3 — Generate tests */}
                <div className={s.step}>
                    <h3 className={s.stepTitle}>3. Generate tests</h3>
                    <div className={s.stepBody}>
                        <p className={s.stepDescription}>
                            Point the agent at a running instance of your app:
                        </p>
                        <div className={s.codeBlock}>
                            <pre className={s.codeText}>
                                <span className={s.syntaxComment}># Set up your project</span>{"\n"}
                                <span className={s.syntaxComment}># --target is the URL of the app under test. It can be a local dev server, a staging URL, or a public endpoint.</span>{"\n"}
                                <span>ata init --target http://localhost:3000</span>{"\n"}
                                {"\n"}
                                <span className={s.syntaxComment}># Create end-to-end tests</span>{"\n"}
                                <span>ata scout</span>
                            </pre>
                            <CopyButton text={generateCode} />
                        </div>
                        <p className={s.stepDescription}>
                            That's it — <code className={s.inlineCode}>scout</code> proposes new tests based on recent changes and coverage gaps.
                        </p>
                    </div>
                </div>

                {/* Commands table */}
                <div className={s.step}>
                    <h3 className={s.stepTitle}>Commands</h3>
                    <div className={s.stepBody}>
                        <table className={s.table}>
                            <thead>
                                <tr>
                                    <th className={s.th}>Command</th>
                                    <th className={s.th}>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {commands.map((row) => (
                                    <tr key={row.cmd}>
                                        <td className={s.td}>
                                            <code className={s.inlineCode}>{row.cmd}</code>
                                        </td>
                                        <td className={s.td}>{row.desc}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Options */}
                <div className={s.step}>
                    <h3 className={s.stepTitle}>Options</h3>
                    <div className={s.stepBody}>
                        <div className={s.codeBlock}>
                            <pre className={s.codeText}>{optionsCode}</pre>
                            <CopyButton text={optionsCode} />
                        </div>
                        <p className={s.stepDescription}>
                            <span>See the </span>
                            <a className={s.stepLink} href={`${DOCS_BASE}/reference/cli/`} target="_blank" rel="noopener noreferrer">
                                CLI reference
                            </a>
                            <span> for the full list.</span>
                        </p>
                    </div>
                </div>

                {/* Divider */}
                <div className={s.cardDivider} />

                {/* Keep going */}
                <div className={s.step}>
                    <h3 className={s.stepTitle}>Keep going</h3>
                    <p className={s.stepDescription} style={{ padding: "0 8px" }}>
                        Ready for the full tour? The end-to-end walkthrough covers installation details, configuration, customizing the agent, and more:
                    </p>
                    <div className={s.keepGoingCard}>
                        <p className={s.keepGoingTitle}>Full walkthrough</p>
                        <p className={s.keepGoingDesc}>Step-by-step guide for first-time users.</p>
                        <div className={s.keepGoingLinks}>
                            <a className={s.stepLink} href={`${DOCS_BASE}/getting-started/`} target="_blank" rel="noopener noreferrer">
                                Getting Started →
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

EmptyStateLanding.Sidebar = GettingStartedSidebar;

export default EmptyStateLanding;
